import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  CardTitle,
  FormGroup,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormFeedback,
  Label,
  Input,
  CardImg,
  Row,
  Col,
  Spinner
} from "reactstrap";
import { useDispatch } from "react-redux";
import { updateUserInfoRequest } from "store/actions";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { FiClock, FiUser, FiHome, FiPhone, FiDollarSign, FiInfo, FiImage, FiFileText, FiPercent } from "react-icons/fi";

const UserModal = ({ user, closeModal, handleUpdateSuccess }) => {
  const [editedUser, setEditedUser] = useState({
    full_name: "",
    salon_name: "",
    location: "",
    mobile: "",
    account_name: "",
    bank_account: "",
    description: "",
    opening_time: "09:00",
    closing_time: "17:00",
    tax_number: "",
    commission: "",
    commercial_register: "",
    entity_unified_no: "",
    ...user
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [img, setImg] = useState(user.profile_img_url);
  const [isEdited, setIsEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setEditedUser({
        full_name: user.full_name || "",
        salon_name: user.salon_name || "",
        location: user.location || "",
        mobile: user.mobile || "",
        account_name: user.account_name || "",
        bank_account: user.bank_account || "",
        description: user.description || "",
        opening_time: user.opening_time || "09:00",
        closing_time: user.closing_time || "17:00",
        tax_number: user.tax_number || "",
        commission: user.commission || "",
        commercial_register: user.commercial_register || "",
        entity_unified_no: user.entity_unified_no || "",
        profile_img_url: user.profile_img_url
      });
      setImg(user.profile_img_url);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser({ ...editedUser, [name]: value });
    setIsEdited(true);
    
    // Clear validation error when user types
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: undefined });
    }
  };

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    const newState = { ...editedUser, [name]: value };
    
    // Auto-adjust closing time if it becomes invalid
    if (name === 'opening_time' && newState.closing_time && value >= newState.closing_time) {
      newState.closing_time = '';
      setValidationErrors({ ...validationErrors, time_range: "وقت الافتتاح يجب أن يكون قبل وقت الإغلاق" });
    } 
    // Auto-adjust opening time if it becomes invalid
    else if (name === 'closing_time' && newState.opening_time && value <= newState.opening_time) {
      newState.opening_time = '';
      setValidationErrors({ ...validationErrors, time_range: "وقت الافتتاح يجب أن يكون قبل وقت الإغلاق" });
    } else if (validationErrors.time_range) {
      setValidationErrors({ ...validationErrors, time_range: undefined });
    }
    
    setEditedUser(newState);
    setIsEdited(true);
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
    
    if (!file) return;

    if (!isFileValid(file)) {
      toast.error("نوع الملف غير صالح. يرجى تحميل صورة (JPEG, PNG, GIF).", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("حجم الصورة كبير جداً (الحد الأقصى 1 ميجابايت)", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setImg(imageUrl);
    setEditedUser({ ...editedUser, img: file });
    setIsEdited(true);
    toast.success("تم تحميل الصورة بنجاح!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const isFileValid = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    return allowedTypes.includes(file.type);
  };

  const validateFormFields = (data) => {
    const errors = {};
    const arabicRegex = /^[\u0600-\u06FF\s]+$/;
    
    if (!data.full_name?.trim()) errors.full_name = "اسم المستخدم مطلوب";
    if(!data.entity_unified_no?.trim()) errors.entity_unified_no = " إثبات العنوان الوطني مطلوب";

    
    if (!data.salon_name?.trim()) errors.salon_name = "اسم الصالون مطلوب";
    if (!data.location?.trim()) errors.location = "عنوان الصالون مطلوب";
    
    if (!data.mobile?.trim()) errors.mobile = "رقم الهاتف مطلوب";
    else if (!/^[0-9]{10,15}$/.test(data.mobile)) errors.mobile = "رقم هاتف غير صالح";
    
    if (!data.account_name?.trim()) errors.account_name = "اسم البنك مطلوب";
    if (!data.bank_account?.trim()) errors.bank_account = "رقم الحساب البنكي مطلوب";
    if (!data.description?.trim()) errors.description = "الوصف مطلوب";
    
    // Tax number validation
    if (!data.tax_number?.trim()) errors.tax_number = "رقم الضريبة مطلوب";
    
    // Commission validation
    if (!data.commission?.toString().trim()) errors.commission = "العمولة مطلوبة";
    else if (isNaN(data.commission)) errors.commission = "العمولة يجب أن تكون رقم صحيح";
    else if (parseFloat(data.commission) < 1 || parseFloat(data.commission) > 100) errors.commission = "العمولة يجب أن تكون بين 1 و 100";
    
    // Commercial register validation
    if (!data.commercial_register?.trim()) errors.commercial_register = "السجل التجاري مطلوب";
    
    // Time validation
    if (!data.opening_time) errors.opening_time = "وقت الافتتاح مطلوب";
    if (!data.closing_time) errors.closing_time = "وقت الإغلاق مطلوب";
    if (data.opening_time && data.closing_time && data.opening_time >= data.closing_time) {
      errors.time_range = "وقت الافتتاح يجب أن يكون قبل وقت الإغلاق";
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateFormFields(editedUser);
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast.error("الرجاء إكمال جميع الحقول المطلوبة بشكل صحيح", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { id, ...updateData } = editedUser;
      await dispatch(updateUserInfoRequest({ id, ...updateData }));
      
      toast.success("تم تحديث البيانات بنجاح", {
        position: "top-right",
        autoClose: 3000,
      });
      
      handleUpdateSuccess?.();
      setTimeout(closeModal, 1000);
    } catch (error) {
      toast.error("حدث خطأ أثناء حفظ التغييرات", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} centered size="lg" backdrop="static">
      <ModalHeader toggle={closeModal}>
        <CardTitle className="d-flex align-items-center">
          <FiUser className="me-2" />
          تعديل الملف الشخصي
        </CardTitle>
      </ModalHeader>
      
      <ModalBody>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Form>
            <Row>
              {/* Personal Information Column */}
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiUser className="me-2" />
                    الاسم الكامل *
                  </Label>
                  <Input
                    type="text"
                    name="full_name"
                    value={editedUser.full_name}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.full_name}
                    disabled={true}
                    placeholder="أدخل الاسم الثلاثي"
                  />
                  <FormFeedback>{validationErrors.full_name}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiPhone className="me-2" />
                    رقم الهاتف *
                  </Label>
                  <Input
                    type="tel"
                    name="mobile"
                    value={editedUser.mobile}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.mobile}
                    disabled={true}
                    placeholder="05XXXXXXXX"
                  />
                  <FormFeedback>{validationErrors.mobile}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiHome className="me-2" />
                    اسم الصالون *
                  </Label>
                  <Input
                    type="text"
                    name="salon_name"
                    value={editedUser.salon_name}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.salon_name}
                    disabled={true}
                    placeholder="أدخل اسم الصالون"
                  />
                  <FormFeedback>{validationErrors.salon_name}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiHome className="me-2" />
                    عنوان الصالون *
                  </Label>
                  <Input
                    type="text"
                    name="location"
                    value={editedUser.location}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.location}
                    // disabled={true}
                    placeholder="أدخل العنوان بالتفصيل"
                  />
                  <FormFeedback>{validationErrors.location}</FormFeedback>
                </FormGroup>

                  <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiFileText className="me-2" />
                    إثبات العنوان الوطني *
                  </Label>
                  <Input
                    type="text"                   
                    name="entity_unified_no"
                    value={editedUser.entity_unified_no}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.entity_unified_no}
                    disabled={true}
                    placeholder="أدخل رقم العنوان الوطني"
                  />
                  <FormFeedback>{validationErrors.entity_unified_no}</FormFeedback>
                </FormGroup>
              </Col>

              {/* Business Information Column */}
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiClock className="me-2" />
                    أوقات العمل *
                  </Label>
                  <Row>
                    <Col sm={6}>
                      <Input
                        type="time"
                        name="opening_time"
                        value={editedUser.opening_time}
                        onChange={handleTimeChange}
                        invalid={!!validationErrors.opening_time}
                      />
                      <FormFeedback>{validationErrors.opening_time}</FormFeedback>
                    </Col>
                    <Col sm={6}>
                      <Input
                        type="time"
                        name="closing_time"
                        value={editedUser.closing_time}
                        onChange={handleTimeChange}
                        invalid={!!validationErrors.closing_time}
                      />
                      <FormFeedback>{validationErrors.closing_time}</FormFeedback>
                    </Col>
                  </Row>
                  {validationErrors.time_range && (
                    <div className="text-danger small mt-2">
                      {validationErrors.time_range}
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiDollarSign className="me-2" />
                    معلومات البنك *
                  </Label>
                  <Input
                    type="text"
                    name="account_name"
                    value={editedUser.account_name}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.account_name}
                    disabled={true}
                    placeholder="اسم البنك"
                    className="mb-2"
                  />
                  <Input
                    type="text"
                    name="bank_account"
                    value={editedUser.bank_account}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.bank_account}
                    disabled={true}
                    placeholder="رقم الحساب البنكي"
                  />
                  <FormFeedback>{validationErrors.account_name || validationErrors.bank_account}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiInfo className="me-2" />
                    الوصف *
                  </Label>
                  <Input
                    type="textarea"
                    name="description"
                    value={editedUser.description}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.description}
                    rows={3}
                    placeholder="وصف الصالون والخدمات المقدمة"
                  />
                  <FormFeedback>{validationErrors.description}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiImage className="me-2" />
                    صورة الملف الشخصي
                  </Label>
                  <Input
                    type="file"
                    name="img"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mb-2"
                  />
                  {img && (
                    <div className="d-flex align-items-center">
                      <CardImg
                        src={img}
                        alt="Profile"
                        className="img-thumbnail rounded-circle"
                        style={{ width: "80px", height: "80px", objectFit: "cover" }}
                      />
                      <span className="ms-2 small text-muted">معاينة الصورة</span>
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/* Additional Business Information Row */}
            <Row className="mt-3 pt-3 border-top">
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiFileText className="me-2" />
                    رقم الضريبة *
                  </Label>
                  <Input
                    type="text"
                    name="tax_number"
                    value={editedUser.tax_number}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.tax_number}
                    disabled={true}
                    placeholder="أدخل رقم الضريبة"
                  />
                  <FormFeedback>{validationErrors.tax_number}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiPercent className="me-2" />
                    العمولة (%) *
                  </Label>
                  <Input
                    type="number"
                    name="commission"
                    value={editedUser.commission}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.commission}
                    placeholder="أدخل نسبة العمولة"
                    min="1"
                    max="100"
                    step="0.1"
                    disabled={true}
                  />
                    <Label className="fw-sm d-flex align-items-center">
                   عمولة المنصة = عمولة التطبيق (10%) + عمولة تشغيل المنصة 
                  </Label>
                  <FormFeedback>{validationErrors.commission}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label className="fw-bold d-flex align-items-center">
                    <FiFileText className="me-2" />
                    السجل التجاري *
                  </Label>
                  <Input
                    type="text"
                    name="commercial_register"
                    value={editedUser.commercial_register}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.commercial_register}
                    disabled={true}
                    placeholder="أدخل رقم السجل التجاري"
                  />
                  <FormFeedback>{validationErrors.commercial_register}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </motion.div>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <Button color="secondary" onClick={closeModal} disabled={isSubmitting}>
          إلغاء
        </Button>
        <Button 
          color="primary" 
          onClick={handleSubmit} 
          disabled={!isEdited || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              جاري الحفظ...
            </>
          ) : (
            "حفظ التغييرات"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UserModal;